import { Box, Modal } from '@mui/material'
import { PaymentAPIs } from 'GlobalStores/Checkout/PaymentServices/PaymentAPI'
import storeCheckout from 'GlobalStores/Checkout/StoreCheckout'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import StorePaymentStep from 'GlobalStores/Cores/Elements/StorePaymentStep'
import { storeElement } from 'GlobalStores/StoreElement'
import storeAuth from 'GlobalStores/User/StoreAuth'
import storeUser from 'GlobalStores/User/StoreUser'
import { get, has } from 'lodash'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const WorldPay3DS = observer(({ challengeHTML, hydraCreateOrderRequestBody, hydraCreateOrderResponseBody, hydraCreateOrderResponseHeader, paymentId, resetPayment }) => {
    const navigate = useNavigate()
    const ddcIframeRef = useRef(null)
    const challengeIframeRef = useRef(null)
    const [ddcIframeReady, setDdcIframeReady] = useState(false)
    const [challengeIframeReady, setChallengeIframeReady] = useState(false)
    const [isDdcIframeOpen, setIsDdcIframeOpen] = useState(false)
    const [isChallengeIframeOpen, setIsChallengeIframeOpen] = useState(false)

    const ddcHTML = storeCheckout.ddcHTML

    const resetIframeStates = useCallback(() => {
        setIsDdcIframeOpen(false)
        setIsChallengeIframeOpen(false)
        setDdcIframeReady(false)
        setChallengeIframeReady(false)
        runInAction(() => {
            storeCheckout.ddcHTML = null
            storeCheckout.challengeHTML = null
        })
    }, [])

    function getParameterByName(name, url) {
        const targetUrl = url || window.location.href

        try {
            const parsedUrl = new URL(targetUrl, window.location.origin)
            return parsedUrl.searchParams.get(name)
        } catch (error) {
            console.error('Invalid redirect URL while parsing parameter', error)
            return null
        }
    }

    const decodeWorldPayHTML = html => {
        if (!html) return ''

        try {
            return decodeURIComponent(html)
        } catch (error) {
            return html
        }
    }

    const toCurl = ({
        method = `POST`,
        url,
        headers = {
            'Content-Type': `application/json`
        },
        dataRaw
    }) => {
        const curlLines = [`curl --location --request ${method} '${url}'`, ...Object.keys(headers).map(key => `--header '${key}: ${headers[key]}'`), `--data-raw '${dataRaw}'`]

        return curlLines.join(` `)
    }

    const maskSensitiveData = data => {
        if (!data) return data
        const maskedData = data

        if (maskedData?.transactions?.method === 'CreditCard') {
            return maskedData
        }

        if (maskedData?.transactions?.items) {
            maskedData.transactions.items = maskedData.transactions.items.map(item => {
                if (item.methodDetails?.creditCardNumber) {
                    return {
                        ...item,
                        methodDetails: {
                            ...item.methodDetails,
                            creditCardNumber: `****${item.methodDetails.creditCardNumber.slice(-4)}`,
                            creditCardExpires: '****-**',
                            creditCardSecurityCode: '***'
                        }
                    }
                }
                return item
            })
        }
        return maskedData
    }

    useEffect(() => {
        const hasDdcHtml = Boolean(ddcHTML)
        setIsDdcIframeOpen(hasDdcHtml)
        if (!hasDdcHtml) {
            setDdcIframeReady(false)
        }
    }, [ddcHTML])

    useEffect(() => {
        const hasChallengeHtml = Boolean(challengeHTML)
        setIsChallengeIframeOpen(hasChallengeHtml)
        if (!hasChallengeHtml) {
            setChallengeIframeReady(false)
        }
    }, [challengeHTML])

    const injectHtmlIntoIframe = (iframeRef, html) => {
        if (!iframeRef?.current || !html) {
            return
        }

        const iframeDocument = iframeRef.current.contentWindow?.document
        if (!iframeDocument) {
            return
        }

        try {
            iframeDocument.open()
            iframeDocument.write(html)
            iframeDocument.close()
        } catch (error) {
            console.error('Failed to inject WorldPay HTML into iframe', error)
        }
    }

    const handleContinuePayment = useCallback(
        async ({ result, orderId }) => {
            resetIframeStates()
            runInAction(() => {
                storeCheckout.checkoutLoading = false
                storeElement.showCardFrontDrop = false
            })
            if (result === true) {
                StorePaymentStep.SetPaymentStep('createOrder', 1)
                const logOrder = {
                    payments_id: paymentId,
                    payment_status: 'success',
                    order_number: orderId,
                    approval_code: '',
                    dist_id: storeAuth.GetId(),
                    dist_status: storeUser.getShortStatus(),
                    hydra_curl: toCurl({
                        method: 'POST',
                        url: `https://hydra.unicity.net/v5a/customers/me/ordersAndSubscriptions?async=false`,
                        headers: { Authorization: `${storeAuth.GetToken()}` },
                        dataRaw: JSON.stringify(maskSensitiveData(hydraCreateOrderRequestBody))
                    }),
                    hydra_response_header: JSON.stringify(hydraCreateOrderResponseHeader),
                    hydra_response_body: JSON.stringify(maskSensitiveData(hydraCreateOrderResponseBody))
                }
                // Subscription was already created by /ordersAndSubscriptions,
                // so never send isCreateSubscribe=1 to hydra-log (would duplicate the autoship)
                await Promise.allSettled([PaymentAPIs.UpdateLog(logOrder, 0)])
                console.log('orderId', orderId)
                StorePaymentStep.SetPaymentStep('updateLog', 1)
                console.log('Payment success')
                setTimeout(() => {
                    navigate(`/payment-response/success?payment_id=${paymentId}`)
                }, 500)
            } else {
                try {
                    const logOrder = {
                        payments_id: paymentId,
                        payment_status: 'fail',
                        order_number: '',
                        approval_code: '',
                        dist_id: storeAuth.GetId(),
                        dist_status: storeUser.getShortStatus(),
                        hydra_curl: toCurl({
                            method: 'POST',
                            url: `https://hydra.unicity.net/v5a/customers/me/ordersAndSubscriptions?async=false`,
                            headers: { Authorization: `${storeAuth.GetToken()}` },
                            dataRaw: JSON.stringify(maskSensitiveData(hydraCreateOrderRequestBody))
                        }),
                        hydra_response_header: JSON.stringify(hydraCreateOrderResponseHeader),
                        hydra_response_body: JSON.stringify(maskSensitiveData(hydraCreateOrderResponseBody))
                    }

                    await Promise.allSettled([PaymentAPIs.UpdateLog(logOrder)])
                    StorePaymentStep.SetPaymentStep('createOrder', -1)
                    StorePaymentStep.SetPaymentStep('updateLog', 1)
                    console.log('Payment failed')
                    resetPayment()
                } catch (error) {
                    console.error('Error updating log', error)
                }
            }
        },
        [hydraCreateOrderRequestBody, hydraCreateOrderResponseBody, hydraCreateOrderResponseHeader, navigate, paymentId, resetIframeStates, resetPayment]
    )

    useEffect(() => {
        if (!isDdcIframeOpen) {
            return
        }

        if (!ddcIframeReady) {
            return
        }

        if (!ddcHTML) {
            return
        }

        injectHtmlIntoIframe(ddcIframeRef, decodeWorldPayHTML(ddcHTML))
    }, [ddcHTML, ddcIframeReady, isDdcIframeOpen])

    useEffect(() => {
        if (!isChallengeIframeOpen) {
            return
        }

        if (!challengeIframeReady) {
            return
        }

        if (!challengeHTML) {
            return
        }

        injectHtmlIntoIframe(challengeIframeRef, decodeWorldPayHTML(challengeHTML))
    }, [challengeHTML, challengeIframeReady, isChallengeIframeOpen])

    useEffect(() => {
        runInAction(() => {
            storeCheckout.checkoutErrors = []
            storeElement.showCardFrontDrop = true
            storeCheckout.checkoutLoading = true
        })

        const createOrderTimeoutId2 = setTimeout(() => {
            PaymentAPIs.UpdateLog(
                {
                    payment_id: paymentId,
                    order_number: get(hydraCreateOrderResponseBody, 'id.unicity', ''),
                    is_timeout: 1
                },
                0,
                storeCountry.Country3()
            ).catch(() => null)
        }, 15000)

        const handleMessage = event => {
            const { data } = event || {}

            if (!data || data === 'PROXY_READY') {
                return
            }

            let parsedMessage

            try {
                parsedMessage = typeof data === 'string' ? JSON.parse(data) : data
            } catch (error) {
                console.error('Invalid WorldPay message payload', error)
                StorePaymentStep.SetErrors(['something_went_wrong_with_payment'])
                handleContinuePayment({ result: false })
                return
            }

            if (parsedMessage?.type === 'WP_3DS_SUCCESS') {
                handleContinuePayment({ result: true, orderId: parsedMessage.order_id })
                return
            }

            if (parsedMessage?.type === 'WP_3DS_FAILED') {
                const worldPayErrorMessage = parsedMessage.error_message || 'something_went_wrong_with_payment'
                StorePaymentStep.SetErrors([worldPayErrorMessage])
                handleContinuePayment({ result: false, orderId: parsedMessage.order_id })
                return
            }

            const redirectURL = parsedMessage?.redirectURL

            if (!redirectURL) {
                return
            }

            const tppPayload = getParameterByName('tppPayload', redirectURL)

            if (!tppPayload) {
                return
            }

            let tppPayloadJson

            try {
                tppPayloadJson = JSON.parse(tppPayload)
            } catch (error) {
                console.error('Invalid WorldPay tppPayload', error)
                StorePaymentStep.SetErrors(['something_went_wrong_with_payment'])
                handleContinuePayment({ result: false })
                return
            }

            const error = get(tppPayloadJson, 'eventDetails.error')
            const errorMessage = get(tppPayloadJson, 'eventDetails.error.error_message')
            const orderId = get(tppPayloadJson, 'eventDetails.id.unicity')

            if (has(tppPayloadJson, 'eventDetails')) {
                if (orderId) {
                    console.log('Payment success and go to success step ', orderId)
                    handleContinuePayment({ result: true, orderId })
                    return
                }

                if (error || errorMessage || error === '') {
                    console.log('Payment failed and go to failed step')
                    const worldPayErrorMessage = errorMessage || 'something_went_wrong_with_payment'
                    StorePaymentStep.SetErrors([worldPayErrorMessage])
                    handleContinuePayment({ result: false, orderId })
                    return
                }

                console.error('WorldPay 3DS returned unexpected error format', error, errorMessage)
                return
            }

            console.log('Redirect URL = ', redirectURL)
            console.log('Redirect URL process to payment =', /review/.test(redirectURL))
            console.log('Order ID = ', orderId)
            console.log('Event details = ', get(tppPayloadJson, 'eventDetails'))
        }

        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [handleContinuePayment, paymentId, hydraCreateOrderResponseBody])

    return (
        <div>
            {isDdcIframeOpen && <iframe ref={ddcIframeRef} src="/wp-iframe.html" style={{ display: 'none' }} title="WorldPay3DSIFrame" onLoad={() => setDdcIframeReady(true)} />}
            {isChallengeIframeOpen && (
                <Modal
                    open={isChallengeIframeOpen}
                    aria-labelledby="modal-modal-chanllenge"
                    aria-describedby="modal-modal-description">
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 420,
                            bgcolor: 'background.paper',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
                        }}>
                        <Box
                            component="button"
                            onClick={resetIframeStates}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 12,
                                background: 'transparent',
                                border: 'none',
                                fontSize: 20,
                                cursor: 'pointer',
                                color: '#555',
                                padding: '4px 8px',
                                zIndex: 1,
                                lineHeight: 1
                            }}
                            aria-label="Close">
                            ✕
                        </Box>
                        <iframe
                            id="wpStepUp"
                            ref={challengeIframeRef}
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation allow-modals allow-popups-to-escape-sandbox allow-presentation"
                            src="/wp-iframe.html"
                            style={{
                                width: '100%',
                                height: '600px',
                                border: 'none',
                                display: 'block',
                                scrollbarWidth: 'none'
                            }}
                            title="3DS Challenge"
                            onLoad={() => setChallengeIframeReady(true)}
                        />
                    </Box>
                </Modal>
            )}
        </div>
    )
})

export default WorldPay3DS
